import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  useTheme,
  alpha,
  Switch,
  FormControlLabel,
  TextField,
} from '@mui/material';
import CropPortraitIcon from '@mui/icons-material/CropPortrait';
import CropLandscapeIcon from '@mui/icons-material/CropLandscape';
import ViewDayIcon from '@mui/icons-material/ViewDay';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';

const PAGE_FORMATS = [
  { value: 'A4', label: 'A4', sub: '210 × 297 mm' },
  { value: 'Letter', label: 'Letter', sub: '216 × 279 mm' },
  { value: 'Legal', label: 'Legal', sub: '216 × 356 mm' },
];

const PAGE_NUMBER_POSITIONS = [
  { value: 'bottom-center', label: 'Bottom Center' },
  { value: 'bottom-right', label: 'Bottom Right' },
  { value: 'bottom-left', label: 'Bottom Left' },
  { value: 'top-center', label: 'Top Center' },
  { value: 'top-right', label: 'Top Right' },
  { value: 'top-left', label: 'Top Left' },
];

function SectionLabel({ children }) {
  const theme = useTheme();
  return (
    <Typography
      variant="overline"
      sx={{
        fontSize: '0.65rem',
        fontWeight: 700,
        letterSpacing: '0.12em',
        color: theme.palette.text.secondary,
        mb: 1.5,
        display: 'block',
      }}
    >
      {children}
    </Typography>
  );
}

function FormatCard({ format, selected, onClick }) {
  const theme = useTheme();
  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 0.75,
        px: 2,
        py: 1.5,
        borderRadius: 1.5,
        border: `1.5px solid ${selected ? theme.palette.primary.main : theme.palette.divider}`,
        backgroundColor: selected
          ? alpha(theme.palette.primary.main, 0.06)
          : theme.palette.background.paper,
        cursor: 'pointer',
        flex: 1,
        transition: 'all 0.15s ease',
        '&:hover': {
          borderColor: theme.palette.primary.main,
          backgroundColor: alpha(theme.palette.primary.main, 0.04),
        },
      }}
    >
      {/* Miniature page icon */}
      <Box
        sx={{
          width: format.value === 'Legal' ? 22 : 24,
          height: format.value === 'A4' ? 34 : format.value === 'Letter' ? 30 : 38,
          border: `1.5px solid ${selected ? theme.palette.primary.main : theme.palette.text.disabled}`,
          borderRadius: '2px',
          backgroundColor: selected
            ? alpha(theme.palette.primary.main, 0.08)
            : theme.palette.background.default,
          transition: 'all 0.15s ease',
        }}
      />
      <Typography
        variant="body2"
        sx={{ fontWeight: selected ? 600 : 400, fontSize: '0.8rem' }}
      >
        {format.label}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
        {format.sub}
      </Typography>
    </Box>
  );
}

function MarginInput({ label, value, onChange }) {
  return (
    <TextField
      label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      size="small"
      type="number"
      inputProps={{ min: 0, max: 10, step: 0.1 }}
      InputProps={{
        endAdornment: <InputAdornment position="end">cm</InputAdornment>,
      }}
      sx={{ flex: 1, minWidth: 80 }}
    />
  );
}

export default function PageSetupPanel({ open, onClose, pageSettings, onApply }) {
  const theme = useTheme();
  const [local, setLocal] = useState(() => ({ ...pageSettings }));

  // Reset local state when dialog opens
  React.useEffect(() => {
    if (open) setLocal({ ...pageSettings });
  }, [open, pageSettings]);

  const merge = (patch) => setLocal((prev) => ({ ...prev, ...patch }));
  const mergeMargin = (side, val) =>
    setLocal((prev) => ({
      ...prev,
      margins: { ...prev.margins, [side]: val },
    }));
  const handleApply = () => {
    onApply(local);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2.5,
          boxShadow: '0 24px 64px rgba(0,0,0,0.14)',
          overflow: 'hidden',
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          px: 3,
          py: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: `1px solid ${theme.palette.divider}`,
          background:
            theme.palette.mode === 'dark'
              ? alpha(theme.palette.primary.main, 0.06)
              : alpha(theme.palette.primary.main, 0.03),
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <ViewDayIcon sx={{ color: 'primary.main', fontSize: 20 }} />
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
              Page Setup
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Configure page format, margins, and header/footer
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 2.5, display: 'flex', flexDirection: 'column', gap: 3 }}>

        {/* Page Format */}
        <Box>
          <SectionLabel>Page Format</SectionLabel>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            {PAGE_FORMATS.map((f) => (
              <FormatCard
                key={f.value}
                format={f}
                selected={local.format === f.value}
                onClick={() => merge({ format: f.value })}
              />
            ))}
          </Box>
        </Box>

        {/* Orientation */}
        <Box>
          <SectionLabel>Orientation</SectionLabel>
          <ToggleButtonGroup
            value={local.orientation}
            exclusive
            onChange={(_, val) => val && merge({ orientation: val })}
            size="small"
            sx={{ gap: 1 }}
          >
            <ToggleButton
              value="portrait"
              sx={{ px: 2.5, py: 1, borderRadius: '8px !important', gap: 1, border: `1px solid ${theme.palette.divider} !important` }}
            >
              <CropPortraitIcon fontSize="small" />
              <Typography variant="body2">Portrait</Typography>
            </ToggleButton>
            <ToggleButton
              value="landscape"
              sx={{ px: 2.5, py: 1, borderRadius: '8px !important', gap: 1, border: `1px solid ${theme.palette.divider} !important` }}
            >
              <CropLandscapeIcon fontSize="small" />
              <Typography variant="body2">Landscape</Typography>
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Margins */}
        <Box>
          <SectionLabel>Margins</SectionLabel>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <MarginInput
              label="Top"
              value={local.margins.top}
              onChange={(v) => mergeMargin('top', v)}
            />
            <MarginInput
              label="Right"
              value={local.margins.right}
              onChange={(v) => mergeMargin('right', v)}
            />
            <MarginInput
              label="Bottom"
              value={local.margins.bottom}
              onChange={(v) => mergeMargin('bottom', v)}
            />
            <MarginInput
              label="Left"
              value={local.margins.left}
              onChange={(v) => mergeMargin('left', v)}
            />
          </Box>
        </Box>

        <Divider />

        {/* Page Numbers */}
        <Box>
          <SectionLabel>Page Numbers</SectionLabel>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={local.showPageNumbers}
                  onChange={(e) => merge({ showPageNumbers: e.target.checked })}
                  color="primary"
                  size="small"
                />
              }
              label={<Typography variant="body2">Show page numbers</Typography>}
              sx={{ mr: 0 }}
            />
            {local.showPageNumbers && (
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Position</InputLabel>
                <Select
                  value={local.pageNumberPosition}
                  label="Position"
                  onChange={(e) => merge({ pageNumberPosition: e.target.value })}
                >
                  {PAGE_NUMBER_POSITIONS.map((p) => (
                    <MenuItem key={p.value} value={p.value}>
                      {p.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        </Box>

      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          borderTop: `1px solid ${theme.palette.divider}`,
          gap: 1,
        }}
      >
        <Button onClick={onClose} variant="text" color="inherit" size="small">
          Cancel
        </Button>
        <Button onClick={handleApply} variant="contained" size="small" disableElevation>
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
}
